import styled from 'styled-components';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { CircleNotifications, Edit, Visibility } from '@mui/icons-material';
import { useFormik } from 'formik';
import { object, boolean, string } from 'yup';
import dayjs from 'dayjs';
import {
  ProgrammeChoice,
  AdditionalDocs,
  ExtraActivities,
  TrainingPage,
  Employment,
  LanguageQualification,
  Merits,
  OtherTest,
  ProfessionalQualification,
  ApplicationCreationSidebar,
  AcademicReferee,
  CurriculumVitae,
  ResearchProposal,
  IdentificationDocument,
  ResearchExperience,
  HKPFSForm,
  DistinctiveScholarshipForm,
  ApplicationDeclaration,
  ProofDocument,
  type ApplicationDeclarationData,
  APPLICATION_MODULE,
  PAGE_TEXT,
  Publications,
  AcademicQualification,
  ApplicationStatus,
  StudyPlan,
  PersonalStatement,
  PROGRAMME_TYPE,
  STUDY_MODE,
  PROGRAMME_NAME,
  ProofDocumentType,
} from './components';
import {
  getApplication,
  useSubmitApplication,
  MeetingConditionStatus,
  updateApplicationItem,
  useRemoveApplicationItem,
} from './graphql';
import { CONDITION_TYPE } from '../Assessment/DRCAssessment/components/ConditionCheck/DRCConditionCheck/constant';
import {
  Loading,
  Alert,
  Accordion,
  type AlertDetails,
  Dialog,
  type DialogDetails,
  UserRole,
  ROLES_MODULE,
  handleRoleNavigation,
  SwitchButton,
} from '../../components';
import {
  enableEditApplication,
  FormModificationMode,
  getSimplifyRole,
  SimplifyRoles,
  APPLICATION_STATUS_TYPE,
} from '../../utils';
import { TokenType, useAuth } from '../../contexts';
import { moduleNotFound } from '../Monitor/components';

export const Application: React.FunctionComponent = () => {
  const { applicationId: id } = useParams();

  const [openAlert, setOpenAlert] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });

  const [loading, setLoading] = useState(false);
  const [isRoleEditMode, setIsRoleEditMode] = useState(false);

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });
  const { user, role } = useAuth();
  const agentRole = isRoleEditMode ? role : ''; // role module enter the page as applicant first
  const isRoleMember = Object.values(ROLES_MODULE).includes(
    role as ROLES_MODULE,
  );

  const [isPreview, setIsPreview] = useState(false);

  // Updated at 06/03/2025, for RPG-1799, opening the second round of scholarship application for 2025-02
  const secondOpenDate = '2025-03-09T16:00:00.000Z';
  const secondCloseDate = '2025-04-01T15:59:59.000Z';
  const scholarshipOverdue: boolean =
    dayjs().isAfter(secondCloseDate) || dayjs().isBefore(secondOpenDate);

  let batchSidebarDisabled = true;
  const {
    applicationData,
    countryList,
    universityList,
    meetConditions,
    remarks: returnRemarks,
    loading: applicationDataLoading,
    error: applicationDataError,
    refetch,
  } = getApplication(id as string);

  const hasRmk = returnRemarks.filter(rmk =>
    rmk.toRole.includes(UserRole.APPLICANT),
  );

  const {
    applicationNo,
    activities,
    publications,
    merits,
    languageQualifications,
    trainings,
    academicReferees,
    otherTests,
    academicQualifications,
    employments,
    professionalQualifications,
    programmeChoice,
    status,
    researchExperiences,
    hkpfsInformation,
    distinctiveScholarships,
    curriculumVitae,
    researchProposal,
    studyPlan,
    personalStatement,
    identificationDocument,
    additionalDocuments,
    proofDocuments,
    declaration,
  } = applicationData;

  const { programmeType, programme, studyMode, entryYear, programmeChoiceId } =
    programmeChoice;
  const { HKPFSReferenceNumber } = hkpfsInformation;

  const isHKPFSApplicant = Boolean(HKPFSReferenceNumber);
  const isDistinctiveApplicant =
    distinctiveScholarships.filter(
      scholarship =>
        scholarship.fundingSource !== '' &&
        scholarship.fundingSource !== undefined,
    ).length > 0;

  // TODO:Only show when select Sep EntrY. May change to align with the academic year setting for HKPFS after academic year setting finished
  const programmeTypeIncludeList = [
    PROGRAMME_TYPE.GENERAL_PROGRAMME,
    PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME,
  ];
  const displayHKPFS =
    programme === PROGRAMME_NAME.DOCTOR_OF_PHILOSOPHY &&
    studyMode === STUDY_MODE.FULL_TIME &&
    programmeTypeIncludeList.includes(programmeType as PROGRAMME_TYPE) &&
    entryYear === '2025-01';
  const displayDistinctiveScholarship =
    programme === PROGRAMME_NAME.DOCTOR_OF_PHILOSOPHY &&
    studyMode === STUDY_MODE.FULL_TIME &&
    programmeType === PROGRAMME_TYPE.GENERAL_PROGRAMME &&
    (entryYear === '2025-02' || entryYear === '2025-01'); // entry year updated at 06/03/2025
  const displayResearchExperience =
    programme === PROGRAMME_NAME.DOCTOR_OF_PHILOSOPHY &&
    studyMode === STUDY_MODE.FULL_TIME &&
    programmeTypeIncludeList.includes(programmeType as PROGRAMME_TYPE);
  const displayStudyPlan =
    programme !== PROGRAMME_NAME.MASTER_OF_PHILOSOPHY &&
    studyMode === STUDY_MODE.FULL_TIME &&
    programmeType !== PROGRAMME_TYPE.GENERAL_PROGRAMME;
  const displayProofDocument =
    Object.values(ROLES_MODULE).includes(role as ROLES_MODULE) ||
    (user?.role[0] === 'applicant' && proofDocuments.length > 0);

  let filteredProofDocuments = [...proofDocuments];
  const currentRole = getSimplifyRole(role as ROLES_MODULE);
  if (
    // for common staff
    currentRole !== SimplifyRoles.AR &&
    currentRole !== SimplifyRoles.GS &&
    currentRole !== SimplifyRoles.APPLICANT
  ) {
    filteredProofDocuments = filteredProofDocuments.filter(
      doc => doc.documentType !== ProofDocumentType.RESTRICTED,
    );
  }
  const displayRules: {
    displayHKPFS: boolean;
    displayResearchExperience: boolean;
    displayStudyPlan: boolean;
    displayDistinctiveScholarship: boolean;
    displayProofDocument: boolean;
  } = {
    displayHKPFS,
    displayResearchExperience,
    displayStudyPlan,
    displayDistinctiveScholarship,
    displayProofDocument,
  };

  const updateApplicationForm = updateApplicationItem();
  const clearHKPFS = async (): Promise<void> => {
    // also clear distinctiveScholarships at the same time

    await handleLoading(async () => {
      try {
        await updateApplicationForm({
          variables: {
            id,
            updateId: '',
            input: {
              hkpfsInformation: {
                applicationId: id,
              },
            },
          },
        });
        refetch();
      } catch (error: any) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
  };

  const clearDistinctiveScholarship = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await updateApplicationForm({
          variables: {
            id,
            updateId: '', // can input any string here
            input: {
              distinctiveScholarships: {
                applicationId: id,
                distinctiveScholarships: [],
              },
            },
          },
        });
        refetch();
      } catch (error: any) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
  };

  const removeApplicationItem = useRemoveApplicationItem();
  const clearResearchExperience = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await removeApplicationItem({
          variables: {
            id,
            input: {
              researchExperienceId: researchExperiences?.[0],
            },
          },
        });
        refetch();
      } catch (error: any) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
  };

  const clearStudyPlan = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await removeApplicationItem({
          variables: {
            id,
            input: {
              additionalDocumentId: studyPlan?.[0]?._id,
            },
          },
        });
        refetch();
      } catch (error: any) {
        setOpenAlert(true);
        setAlertDetails({
          alertContent: error.message,
          alertType: 'error',
        });
      }
    });
  };

  useEffect(() => {
    if (
      !applicationDataLoading &&
      !displayResearchExperience &&
      researchExperiences?.length > 0
    ) {
      void clearResearchExperience();
    }
  }, [displayResearchExperience]);

  useEffect(() => {
    if (!applicationDataLoading && !displayStudyPlan && studyPlan?.length > 0) {
      void clearStudyPlan();
    }
  }, [displayStudyPlan]);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  let sidebarActivated = searchParams.get('apsb') as APPLICATION_MODULE;
  if (!Object.values(APPLICATION_MODULE).includes(sidebarActivated)) {
    sidebarActivated = APPLICATION_MODULE.PROGRAMME_CHOICES;
  }

  const [activatedModuleId, setActivatedModuleId] = useState(sidebarActivated);

  useEffect(() => {
    // delete unnecessary param
    const keys = Array.from(searchParams.keys());
    keys.forEach(key => {
      if (key !== 'aphb' && key !== 'apsb') {
        searchParams.delete(key);
      }
    });

    if (activatedModuleId !== APPLICATION_MODULE.APPLICATION_PROGRESS) {
      searchParams.delete('aphb');
    }

    // reset unreachable param
    if (!applicationModulesMap[sidebarActivated]?.reachable) {
      searchParams.set(
        'apsb',
        displayApplicationProgress
          ? APPLICATION_MODULE.APPLICATION_PROGRESS
          : APPLICATION_MODULE.PROGRAMME_CHOICES,
      );
    } else {
      searchParams.set('apsb', activatedModuleId);
    }

    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [activatedModuleId, location.pathname]);

  // programmeChoice created, then other modules in side bar turn to clickable
  if (programmeChoiceId as string) batchSidebarDisabled = false;

  const { status: conditionStatus, conditions } = meetConditions;
  const isConditionEditable = [
    MeetingConditionStatus.CREATED,
    MeetingConditionStatus.MODIFICATION_REQUIRED,
  ].includes(conditionStatus as MeetingConditionStatus);

  const isLanguageEditable =
    isConditionEditable &&
    conditions.some(
      condition => condition === CONDITION_TYPE.ENGLISH_REQUIREMENT,
    );
  const isAcademicQualificationEditable =
    isConditionEditable &&
    conditions.some(
      condition => condition === CONDITION_TYPE.GENERAL_REQUIREMENT,
    );

  const isOtherTestEditable =
    isConditionEditable &&
    conditions.some(
      condition => condition === CONDITION_TYPE.OTHERS_REQUIREMENT,
    );

  const isAdditionalDocumentEditable =
    isConditionEditable &&
    conditions.some(
      condition => condition === CONDITION_TYPE.ADDITIONAL_REQUIREMENT,
    );

  const handleChangeActivated = (activatedId: APPLICATION_MODULE): void => {
    setActivatedModuleId(activatedId);
    window.scrollTo(0, 0);
  };

  const enableEdit = enableEditApplication(
    agentRole as ROLES_MODULE,
    status as APPLICATION_STATUS_TYPE,
    {
      entryYear,
      isAcademicQualificationEditable,
      isLanguageEditable,
      isOtherTestEditable,
      isAdditionalDocumentEditable,
      isPreview,
      isDistinctiveApplicant,
    },
  );

  const enableSubmit = enableEdit.enableSubmit as boolean;
  const enableWithdraw = enableEdit.enableWithdraw as boolean;
  const enableDelete = enableEdit.enableDelete;

  const displayApplicationProgress =
    !(status === APPLICATION_STATUS_TYPE.CREATED) && Boolean(status);

  const declarationFormik = useFormik<ApplicationDeclarationData>({
    initialValues: declaration,
    validationSchema: object({
      confirmation: boolean().required().oneOf([true]),
      informationAccuracy: boolean().required().oneOf([true]),
      personalType: string().required(),
      engagement: boolean().required(),
    }),
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!applicationDataLoading) {
      void declarationFormik.setValues(declaration);
      if (displayApplicationProgress) {
        setActivatedModuleId(APPLICATION_MODULE.APPLICATION_PROGRESS);
      }
    }
  }, [applicationDataLoading]);

  // A withLoading HOC wraps any component,
  // providing it with a handleLoading function
  // to manage the loading logic.
  // The loading spinner (<Loading />) is shown automatically while the operation runs.
  const handleLoading = async (
    callback: () => Promise<void>,
  ): Promise<void> => {
    setLoading(true);
    try {
      await callback();
    } finally {
      setLoading(false);
    }
  };

  // isConditionRequirement indicates whether the module is conditional required form
  const applicationModulesMap: Partial<
    Record<
      APPLICATION_MODULE,
      {
        component: React.ReactNode;
        moduleTitle: string;
        hasForms: boolean;
        enableEdit: boolean;
        reachable: boolean;
      }
    >
  > = {
    [APPLICATION_MODULE.APPLICATION_PROGRESS]: {
      component: (
        <ApplicationStatus
          applicationNo={applicationNo}
          entryYear={entryYear}
          applicationStatus={status}
          refetchApplication={refetch}
          handleChangeApplicationActivated={handleChangeActivated}
        />
      ),
      moduleTitle: '',
      hasForms: true, // used for making sure always present
      enableEdit: true,
      reachable: displayApplicationProgress,
    },
    [APPLICATION_MODULE.ADDITIONAL_DOCUMENTS]: {
      component: (
        <AdditionalDocs
          applicationNo={applicationNo}
          additionalDocuments={additionalDocuments}
          isConditionEditable={isAdditionalDocumentEditable}
          refetchApplication={refetch}
          enableDelete={enableDelete}
          enableEdit={enableEdit.additionalDocuments}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.ADDITIONAL_DOCUMENTS.TITLE,
      hasForms: additionalDocuments.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.RESEARCH_EXPERIENCES]: {
      component: (
        <ResearchExperience
          researchExperienceId={researchExperiences?.[0]}
          isHKPFSApplicant={isHKPFSApplicant}
          enableEdit={enableEdit.commonForms}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.RESEARCH_EXPERIENCE.TITLE,
      hasForms: true,
      enableEdit: enableEdit.commonForms,
      reachable: displayResearchExperience,
    },
    [APPLICATION_MODULE.HKPFS]: {
      component: (
        <HKPFSForm
          HKPFSReferenceNumber={HKPFSReferenceNumber}
          refetchApplication={refetch}
          enableEdit={enableEdit.commonForms}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.FELLOWSHIP.HKPFS.HKPFS_NAME,
      hasForms: true,
      enableEdit: enableEdit.commonForms,
      reachable: displayHKPFS,
    },
    [APPLICATION_MODULE.EXTRA_ACTIVITIES]: {
      component: (
        <ExtraActivities
          applicationNo={applicationNo}
          extraActivityIds={activities}
          enableDelete={enableDelete}
          enableEdit={enableEdit.commonForms}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.EXTRA_ACTIVITY.TITLE,
      hasForms: activities.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.PUBLICATIONS]: {
      component: (
        <Publications
          applicationNo={applicationNo}
          publications={publications}
          enableEdit={enableEdit.publication}
          enableDelete={enableDelete}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.PUBLICATION.TITLE,
      hasForms: publications.length > 0,
      enableEdit:
        enableEdit.publication.formModificationMode !==
          FormModificationMode.DISABLE &&
        enableEdit.publication.formModificationMode !==
          FormModificationMode.CONDITION_MEET,
      reachable: true,
    },
    [APPLICATION_MODULE.MERITS]: {
      component: (
        <Merits
          applicationNo={applicationNo}
          meritIds={merits}
          countryList={countryList}
          enableEdit={enableEdit.commonForms}
          enableDelete={enableDelete}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.MERIT.TITLE,
      hasForms: merits.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.LANGUAGE_QUALIFICATIONS]: {
      component: (
        <LanguageQualification
          applicationNo={applicationNo}
          languageQualification={languageQualifications}
          enableEdit={enableEdit.languageQualification}
          handleLoading={handleLoading}
          enableDelete={enableDelete}
          refetchApplication={refetch}
        />
      ),
      moduleTitle: PAGE_TEXT.QUALIFICATION.LANGUAGE_QUALIFICATION.TITLE,
      hasForms: true,
      enableEdit:
        enableEdit.languageQualification.formModificationMode !==
        FormModificationMode.DISABLE,
      reachable: true,
    },
    [APPLICATION_MODULE.TRAINING_EXPERIENCES]: {
      component: (
        <TrainingPage
          applicationNo={applicationNo}
          countryList={countryList}
          trainingIds={trainings}
          enableEdit={enableEdit.commonForms}
          enableDelete={enableDelete}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.TRAINING.TITLE,
      hasForms: trainings.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.ACADEMIC_REFEREES]: {
      component: (
        <AcademicReferee
          academicRefereeIds={academicReferees}
          enableEdit={enableEdit.academicReferee}
          enableDelete={enableDelete}
          handleLoading={handleLoading}
          refetchApplication={refetch}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.ACADEMIC_REFEREE.TITLE,
      hasForms: academicReferees.length > 0,
      enableEdit: enableEdit.academicReferee,
      reachable: true,
    },
    [APPLICATION_MODULE.OTHER_TESTS]: {
      component: (
        <OtherTest
          applicationNo={applicationNo}
          otherTests={otherTests}
          isConditionEditable={isOtherTestEditable}
          enableEdit={enableEdit.otherTest}
          enableDelete={enableDelete}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.QUALIFICATION.OTHER_TEST.TITLE,
      hasForms: otherTests.length > 0,
      enableEdit:
        enableEdit.otherTest.formModificationMode !==
        FormModificationMode.DISABLE,
      reachable: true,
    },
    [APPLICATION_MODULE.EMPLOYMENT]: {
      component: (
        <Employment
          applicationNo={applicationNo}
          employmentIds={employments}
          enableEdit={enableEdit.commonForms}
          refetchApplication={refetch}
          enableDelete={enableDelete}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.EMPLOYMENT.TITLE,
      hasForms: employments.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.ACADEMIC_QUALIFICATIONS]: {
      component: (
        <AcademicQualification
          applicationNo={applicationNo}
          academicQualifications={academicQualifications}
          countryList={countryList}
          entryYear={entryYear}
          universityList={universityList}
          enableDelete={enableDelete}
          enableEdit={enableEdit.academicQualification}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION.TITLE,
      hasForms: academicQualifications.length > 0,
      enableEdit:
        enableEdit.academicQualification.formModificationMode !==
        FormModificationMode.DISABLE,
      reachable: true,
    },
    [APPLICATION_MODULE.PROGRAMME_CHOICES]: {
      component: (
        <ProgrammeChoice
          programmeChoice={programmeChoice}
          // prog choice can not be edited in return mode
          enableEdit={enableEdit.programmeChoice || id === undefined}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.PROGRAMME.TITLE,
      hasForms: true, // programme choice is always present
      enableEdit: enableEdit.programmeChoice,
      reachable: true,
    },
    [APPLICATION_MODULE.PROFESSIONAL_QUALIFICATIONS]: {
      component: (
        <ProfessionalQualification
          applicationNo={applicationNo}
          professionalQualificationIds={professionalQualifications}
          enableEdit={enableEdit.commonForms}
          enableDelete={enableDelete}
          handleLoading={handleLoading}
          refetchApplication={refetch}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.PROFESSIONAL_QUALIFICATION.TITLE,
      hasForms: professionalQualifications.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.CURRICULUM_VITAE]: {
      component: (
        <CurriculumVitae
          enableDelete={enableDelete}
          applicationNo={applicationNo}
          enableEdit={enableEdit.commonForms}
          docsData={curriculumVitae}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.CURRICULUM_VITAE.TITLE,
      hasForms: curriculumVitae.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.PERSONAL_STATEMENT]: {
      component: (
        <PersonalStatement
          enableDelete={enableDelete}
          applicationNo={applicationNo}
          enableEdit={enableEdit.commonForms}
          docsData={personalStatement}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.ADDITIONAL_DOCUMENTS.PERSONAL_STATEMENT,
      hasForms: personalStatement.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.RESEARCH_PROPOSAL]: {
      component: (
        <ResearchProposal
          applicationNo={applicationNo}
          docsData={researchProposal}
          enableDelete={enableDelete}
          enableEdit={enableEdit.commonForms}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.RESEARCH_PROPOSAL.TITLE,
      hasForms: researchProposal.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.IDENTIFICATION_DOCUMENT]: {
      component: (
        <IdentificationDocument
          applicationNo={applicationNo}
          docsData={identificationDocument}
          enableEdit={enableEdit.commonForms}
          refetchApplication={refetch}
          handleLoading={handleLoading}
          enableDelete={enableDelete}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.IDENTIFICATION_DOCUMENT.TITLE,
      hasForms: identificationDocument.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.STUDY_PLAN]: {
      component: (
        <StudyPlan
          enableDelete={enableDelete}
          applicationNo={applicationNo}
          docsData={studyPlan}
          enableEdit={enableEdit.commonForms}
          refetchApplication={refetch}
          handleLoading={handleLoading}
        />
      ),
      moduleTitle: PAGE_TEXT.OTHER_INFORMATION.RESEARCH_PROPOSAL.TITLE,
      hasForms: studyPlan.length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: displayStudyPlan,
    },
    [APPLICATION_MODULE.DISTINCTIVE_SCHOLARSHIP]: {
      component: (
        <DistinctiveScholarshipForm
          distinctiveScholarships={distinctiveScholarships}
          entryYear={entryYear}
          applicationNo={applicationNo}
          enableEdit={enableEdit.distinctiveScholarship}
          refetchApplication={refetch}
          handleLoading={handleLoading}
          enableDelete={enableDelete}
        />
      ),
      moduleTitle: PAGE_TEXT.FELLOWSHIP.DISTINCTIVE_SCHOLARSHIP.TITLE,
      hasForms:
        (distinctiveScholarships ?? []).filter(
          scholarship =>
            scholarship.fundingSource !== '' &&
            scholarship.fundingSource !== undefined,
        ).length > 0,
      enableEdit: enableEdit.commonForms,
      reachable: displayDistinctiveScholarship,
    },
    [APPLICATION_MODULE.DECLARATION]: {
      component: (
        <ApplicationDeclaration
          formik={declarationFormik}
          enableEdit={enableEdit.commonForms}
        />
      ),
      moduleTitle: PAGE_TEXT.DECLARATION.TITLE,
      hasForms: true,
      enableEdit: enableEdit.commonForms,
      reachable: true,
    },
    [APPLICATION_MODULE.PROOF_DOCUMENT]: {
      component: (
        <ProofDocument
          proofDocuments={filteredProofDocuments}
          applicationNo={applicationNo}
          enableEdit={enableEdit.proofOfDocument}
          enableDelete={enableDelete}
          handleLoading={handleLoading}
          refetchApplication={refetch}
        />
      ),
      moduleTitle: PAGE_TEXT.PROOF_DOCUMENT.TITLE,
      hasForms: filteredProofDocuments.length > 0,
      enableEdit:
        enableEdit.proofOfDocument.formModificationMode !==
        FormModificationMode.DISABLE,
      reachable: displayProofDocument,
    },
  };

  const handleRefetchAndPreviewApplication = async (): Promise<void> => {
    await handlePreviewApplication();
  };

  const handlePreviewApplication = async (): Promise<void> => {
    // The order of conditions are following te the orders of modules in the sidebar
    declarationFormik.handleSubmit();
    const isDeclarationValid = await declarationFormik.validateForm();
    const declarationErrors = Object.keys(isDeclarationValid);
    const conditions = [
      {
        condition: academicReferees.length > (isHKPFSApplicant ? 1 : 0), // At least 2 academic referees for HKPFS applicants
        alertContent: isHKPFSApplicant
          ? PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_ACADEMIC_REFEREE_2
          : PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_ACADEMIC_REFEREE_1,
        moduleId: APPLICATION_MODULE.ACADEMIC_REFEREES,
      },
      {
        condition: academicQualifications.length > 0,
        alertContent:
          PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_ACADEMIC_QUALIFICATION,
        moduleId: APPLICATION_MODULE.ACADEMIC_QUALIFICATIONS,
      },
      {
        condition: languageQualifications.needEnglishTest !== undefined,
        alertContent:
          PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_LANGUAGE_QUALIFICATION,
        moduleId: APPLICATION_MODULE.LANGUAGE_QUALIFICATIONS,
      },
      ...(displayResearchExperience
        ? [
            {
              condition: researchExperiences.length > 0,
              alertContent:
                PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_RESEARCH_EXPERIENCE,
              moduleId: APPLICATION_MODULE.RESEARCH_EXPERIENCES,
            },
          ]
        : []),
      {
        condition: identificationDocument.length > 0,
        alertContent:
          PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_IDENTIFICATION_DOCUMENT,
        moduleId: APPLICATION_MODULE.IDENTIFICATION_DOCUMENT,
      },
      {
        condition: curriculumVitae.length > 0,
        alertContent: PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_CURRICULUM_VITAE,
        moduleId: APPLICATION_MODULE.CURRICULUM_VITAE,
      },
      {
        condition: researchProposal.length > 0,
        alertContent: PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_RESEARCH_PROPOSAL,
        moduleId: APPLICATION_MODULE.RESEARCH_PROPOSAL,
      },
      {
        condition: personalStatement.length > 0,
        alertContent: PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_PERSONAL_STATEMENT,
        moduleId: APPLICATION_MODULE.PERSONAL_STATEMENT,
      },
      ...(displayStudyPlan
        ? [
            {
              condition: studyPlan.length > 0,
              alertContent: PAGE_TEXT.VALIDATION_SUBMIT.PROVIDE_GSB_J4,
              moduleId: APPLICATION_MODULE.STUDY_PLAN,
            },
          ]
        : []),
      {
        condition: declarationErrors.length === 0,
        alertContent: PAGE_TEXT.VALIDATION_SUBMIT.DECLARATION,
        moduleId: APPLICATION_MODULE.DECLARATION,
      },
    ];

    for (const { condition, alertContent, moduleId } of conditions) {
      if (!condition) {
        setAlertDetails({
          alertContent,
          alertType: 'warning',
        });
        setOpenAlert(true);
        setActivatedModuleId(moduleId);

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
        return;
      }
    }
    setActivatedModuleId(APPLICATION_MODULE.PROGRAMME_CHOICES);
    setIsPreview(!isPreview);
  };

  const handleCloseDialog = (): void => {
    setDialogOpen(false);
  };

  const notAllowSubmitScholarship =
    scholarshipOverdue && status !== APPLICATION_STATUS_TYPE.RETURNED;
  const allowSubmitDistinctive =
    status === APPLICATION_STATUS_TYPE.RETURNED ||
    (!scholarshipOverdue && entryYear === '2025-02');
  const needClearHKPFS = notAllowSubmitScholarship && isHKPFSApplicant;
  const needClearDistinctiveScholarship =
    !allowSubmitDistinctive && isDistinctiveApplicant;
  const needClearScholarship =
    needClearHKPFS || needClearDistinctiveScholarship;

  const handleSubmitApplication = (): void => {
    setDialogOpen(true);
    setDialogDetails(dialogMappings[PAGE_TEXT.SUBMIT_CONFIRM.KEY]);
  };

  const submitApplication = useSubmitApplication();
  const confirmSubmitApplication = async (): Promise<void> => {
    setDialogOpen(false);
    await handleLoading(async () => {
      try {
        // clear funding information if the deadline is overdue
        if (needClearHKPFS) {
          await clearHKPFS();
        }
        if (needClearDistinctiveScholarship) {
          await clearDistinctiveScholarship();
        }
        // update declaration
        await updateApplicationForm({
          variables: {
            id,
            updateId: '',
            input: {
              declaration: {
                ...declarationFormik.values,
                applicationId: id,
              },
            },
          },
        });

        await submitApplication({
          variables: { id },
        });
        setDialogDetails(dialogMappings[PAGE_TEXT.VALIDATION_SUBMIT.KEY]);
        setDialogOpen(true);
      } catch (e) {
        setDialogOpen(false);
        setAlertDetails({
          alertContent: `Submission Failed.${(e as Error)?.message}`,
          alertType: 'error',
        });
        setOpenAlert(true);
      }
    });
  };

  const handleRouteChange = (): void => {
    // after submitting successfully
    refetch();
    window.location.href =
      '/application/' + id + `?apsb=${APPLICATION_MODULE.APPLICATION_PROGRESS}`;
  };

  const dialogMappings: Record<string, DialogDetails> = {
    [PAGE_TEXT.SUBMIT_CONFIRM.KEY]: {
      title: PAGE_TEXT.SUBMIT_CONFIRM.TITLE,
      content: needClearScholarship
        ? PAGE_TEXT.SUBMIT_CONFIRM.CONFIRM_CLEAR_FUNDING
        : PAGE_TEXT.SUBMIT_CONFIRM.CONTENT,
      confirmAction: PAGE_TEXT.SUBMIT,
      cancelAction: PAGE_TEXT.CANCEL,
      handleDialogConfirmAction: confirmSubmitApplication,
    },
    [PAGE_TEXT.VALIDATION_SUBMIT.KEY]: {
      title: '',
      content: !hasRmk.length
        ? PAGE_TEXT.VALIDATION_SUBMIT.SUBMIT_SUCCESS
        : PAGE_TEXT.VALIDATION_SUBMIT.RE_SUBMIT_SUCCESS,
      confirmAction: PAGE_TEXT.YES,
      handleDialogConfirmAction: handleRouteChange,
    },
  };

  const handleBack = (): void => {
    const isStaffView = user?.tokenType === TokenType.INTERNAL;

    // For staff view, go back to the staff home page
    if (isStaffView) {
      if (role !== null) handleRoleNavigation(role);
      return;
    }
    refetch();
    if (isPreview) {
      setIsPreview(false);
      setActivatedModuleId(APPLICATION_MODULE.PROGRAMME_CHOICES);
      return;
    }
    window.location.href = '/applications';
  };

  const activatedModule:
    | {
        component: React.ReactNode;
        moduleTitle: string;
        hasForms: boolean;
        enableEdit: boolean;
        reachable: boolean;
      }
    | undefined = applicationModulesMap[activatedModuleId];

  if (activatedModule?.reachable === false) {
    setActivatedModuleId(APPLICATION_MODULE.PROGRAMME_CHOICES);
  }
  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const displayEditBlock =
    isRoleMember &&
    activatedModuleId !== APPLICATION_MODULE.APPLICATION_PROGRESS;

  if (applicationDataError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` + (applicationDataError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <>
      {loading && <Loading />}

      {applicationDataLoading ? (
        <Loading />
      ) : (
        <PageContainer>
          <div className="sidebar-area">
            <ApplicationCreationSidebar
              status={status}
              sidebarDisabled={batchSidebarDisabled}
              manuallyActivatedId={activatedModuleId}
              isPreview={isPreview}
              enableWithdraw={enableWithdraw && id !== undefined}
              enableSubmit={enableSubmit}
              displayRules={displayRules}
              onChangeActivated={handleChangeActivated}
              onSubmitApplication={handleSubmitApplication}
              onPreviewApplication={handleRefetchAndPreviewApplication}
              onBack={handleBack}
            />
          </div>
          <div className="form-area">
            {displayEditBlock && (
              <div className="fixed z-50 p-3 pl-8 w-full bg-[#ffffff] shadow-md flex items-center">
                {isRoleEditMode ? (
                  <Edit
                    sx={{
                      color: '#ffffff !important',
                      padding: '0.2rem',
                      borderRadius: '50%',
                      background: '#951D1D',
                      marginRight: '0.5rem',
                    }}
                  />
                ) : (
                  <Visibility
                    sx={{
                      color: '#ffffff !important',
                      padding: '0.2rem',
                      borderRadius: '50%',
                      background: '#951D1D',
                      marginRight: '0.5rem',
                    }}
                  />
                )}

                <span className="w-24 flex flex-row font-medium text-[#951D1D]">
                  {isRoleEditMode ? PAGE_TEXT.EDIT_MODE : PAGE_TEXT.VIEW_MODE}
                </span>
                <SwitchButton
                  value={isRoleEditMode}
                  onChange={() => {
                    setIsRoleEditMode(!isRoleEditMode);
                  }}
                />
              </div>
            )}
            <div className={`${displayEditBlock && 'mt-12'} p-4`}>
              {activatedModule?.reachable === true ? (
                activatedModule?.enableEdit ? (
                  moduleComponent
                ) : activatedModule?.hasForms ? (
                  moduleComponent
                ) : (
                  <Accordion
                    title={activatedModule?.moduleTitle ?? ''}
                    defaultExpanded
                    details={
                      <BlankNotice>
                        <CircleNotifications />
                        <p>{PAGE_TEXT.NO_INFORMATION_SUBMITTED}</p>
                      </BlankNotice>
                    }
                  />
                )
              ) : (
                moduleNotFound('Unreachable Section')
              )}
            </div>
          </div>
        </PageContainer>
      )}

      <Dialog
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={handleCloseDialog}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>

      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
    </>
  );
};

const PageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;

  .sidebar-area {
    width: 19rem;
    border-right: 0.1rem solid #eee;
    padding-right: 0.1rem;
  }

  h1 {
    margin-top: 2rem;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    text-align: center;
  }

  .form-area {
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-height: 100vh;
  }
`;

const BlankNotice = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 1rem;
  svg {
    color: #951d1d;
    margin-right: 1rem;
  }
`;
